import {Box} from "@mui/material";
import Typography from "@mui/material/Typography";
import React from "react";
import ReactMarkdown, {Components} from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import {renderTemplateVariables} from "../shared/surveyMarkdownText.util";

interface Props {
    markdown: string
    fontSize?: number
    color?: string
    /** The welcome and end screens are centred; a paragraph's own stored alignment still wins. */
    align?: 'start' | 'center'
}

/*
    The editor's rich text, read the way it is written (common/newRichText): a paragraph keeps the alignment stored
    on it, a blank line is a paragraph holding a no-break space, links open in a new tab. Not the shared MdRenderer,
    whose paragraphs drop their alignment. The renderers live at module level, so React keeps them between renders.
*/
function Paragraph({children, style}: React.ComponentPropsWithoutRef<'p'>) {
    return <Typography variant={'body2'} style={style}>{children}</Typography>;
}

function ListItem({children}: React.ComponentPropsWithoutRef<'li'>) {
    return <Typography component={'li'} variant={'body2'}>{children}</Typography>;
}

function Link({href, children}: React.ComponentPropsWithoutRef<'a'>) {
    return <a href={href} target={'_blank'} rel={'noopener noreferrer'}>{children}</a>;
}

const COMPONENTS: Components = {p: Paragraph, li: ListItem, a: Link};

function SurveyRunnerMarkdown({markdown, fontSize, color, align = 'start'}: Props) {
    const resolved = renderTemplateVariables(markdown);

    if (!resolved.trim()) return null;

    return (
        <Box
            sx={{
                color,
                textAlign: align,
                overflowWrap: 'anywhere',
                '& p': {fontSize, m: 0},
                '& p + p': {mt: 1},
                '& ul, & ol': {pl: 3, my: 1},
                '& li': {fontSize},
            }}
        >
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={COMPONENTS}>
                {resolved}
            </ReactMarkdown>
        </Box>
    );
}

export default SurveyRunnerMarkdown;
